import { useNavigation } from "react-router";
import { type ClassValue, clsx } from "clsx";
import * as cookie from "cookie-es";
import { twMerge } from "tailwind-merge";
import { omitBy } from "remeda";

export function pickDefined<
  const T extends Record<string, string | undefined | number | null | boolean>,
>(
  obj: T,
): {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
} & {
  [K in keyof T as undefined extends T[K] ? K : never]?: Exclude<T[K], undefined>;
} {
  // Claude Sonnet 4.5 says:
  // TypeScript can't prove these types are compatible,
  // even though we know at runtime they will be. The 'as any'
  // acts as a type bridge, telling TypeScript "trust me, I've designed this correctly."
  return omitBy(obj, (value) => value === undefined) as any;
}

export function isSidebarOpen(request: Request) {
  const cookies = cookie.parse(request.headers.get("cookie") ?? "");
  return cookies["sidebar:state"] !== "false";
}

export function getEditCodeModeFromCookie(request: Request) {
  const cookies = cookie.parse(request.headers?.get("cookie") ?? "");
  return cookies["edit-code-mode"] === "true";
}

export async function timeout(timeInMs: number) {
  return new Promise((resolve) => setTimeout(resolve, timeInMs));
}

export async function batch<T, K>(
  items: T[],
  {
    batchSize = 1000,
    batchHandler,
  }: {
    batchSize?: number;
    batchHandler: (batch: T[], itemsProcessed: number) => Promise<K>;
  },
) {
  const results = [] as K[];
  for (let i = 0; i < items.length; i += batchSize) {
    const batchItems = items.slice(i, i + batchSize);
    const result = await batchHandler(batchItems, i);
    results.push(result);
  }
  return results;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const NOT_FOUND_RESPONSE = new Response(null, {
  status: 404,
  statusText: "Not Found",
});

export function throw404() {
  throw NOT_FOUND_RESPONSE;
}

export function throw403() {
  throw new Response(null, {
    status: 403,
    statusText: "Forbidden",
  });
}

// Convert base-64 encoded string back to original UTF-8 string
// The yin to /chat/src/helpers/openai.ts > toBinary's yang
// See https://stackoverflow.com/a/30106551
export function fromBinary(encoded: string) {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return String.fromCharCode(...new Uint16Array(bytes.buffer));
}

// Convert base-64 encoded string to binary blob
// Source: https://gist.github.com/Marceloalves6/d1f590437f7fe45ac6832e5177c84be6
export function base64toBlob(base64Data: string, contentType: string) {
  contentType = contentType || "";
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
}

export function getBaseUrl(url: string) {
  const scheme = url.split("://")[0];
  const host = url.split("://")[1].split("/")[0];
  return `${scheme}://${host}`;
}

export function useLatestFormSubmission<T>(): T {
  const navigation = useNavigation();
  return (navigation.formData ? Object.fromEntries(navigation.formData) : {}) as unknown as T;
}

/**
 * Promise.all for objects
 * @param obj where each value is a promise
 * @returns promise that when awaited resolves to an object with the same keys as obj and values that are the resolved values of the promises
 *
 * @example
 *
 * const obj = {
 *  a: (async () => db.getSomeDBValue())(),
 *  b: (async () => db.getSomeOtherDBValue())()
 * };
 *
 * const resolved = await promiseAllObject(obj);
 * console.log(resolved); // { a: someDBValue, b: someOtherDBValue }
 *
 * Useful for loaders if you want to quickly load several different pieces of data in parallel
 * and pass them to the route.
 */
/**
 * Like Promise.all but for objects. Accepts an object with values that are either
 * promises or raw values, and returns a promise that resolves to an object with
 * the same keys but with all promises resolved.
 *
 * @example
 * const result = await promiseAllObject({
 *   user: fetchUser(),
 *   isAdmin: false,
 *   posts: fetchPosts(),
 * });
 * // result: { user: User, isAdmin: false, posts: Post[] }
 */
export function promiseAllObject<T extends Record<string, unknown>>(
  obj: T,
): Promise<{ [K in keyof T]: Awaited<T[K]> }> {
  const keys = Object.keys(obj) as (keyof T)[];
  return Promise.all(keys.map((key) => Promise.resolve(obj[key]))).then((values) => {
    const result = {} as { [K in keyof T]: Awaited<T[K]> };
    keys.forEach((key, i) => {
      result[key] = values[i] as Awaited<T[typeof key]>;
    });
    return result;
  });
}

export function areSetsEqual<T>(a: Set<T>, b: Set<T>) {
  return a.size === b.size && [...a].every((value) => b.has(value));
}

export const isNavigationWithinRouteGroups = ({
  routeGroupRegexStrings,
  currentUrl,
  nextUrl,
}: {
  routeGroupRegexStrings: string[];
  currentUrl: URL;
  nextUrl: URL;
}) => {
  return routeGroupRegexStrings.some((routeGroupRegexString) =>
    isNavigationWithinRouteGroup({
      routeGroupRegexString,
      currentUrl,
      nextUrl,
    }),
  );
};

export const isNavigationWithinRouteGroup = ({
  routeGroupRegexString,
  currentUrl: { pathname: currentPathname },
  nextUrl: { pathname: nextPathname },
}: {
  routeGroupRegexString: string;
  currentUrl: URL;
  nextUrl: URL;
}) => {
  const routeGroupRegex = new RegExp(routeGroupRegexString);
  const isNextUrlInRouteGroup = routeGroupRegex.test(nextPathname);
  const currentRouteGroupPath = (currentPathname.match(routeGroupRegex) || [])[0];
  const nextRouteGroupPath = (nextPathname.match(routeGroupRegex) || [])[0];

  return isNextUrlInRouteGroup && currentRouteGroupPath === nextRouteGroupPath;
};

/**
 * Format array of simple 1d objects into a tsv string for download
 */
export function arrayToTSV<T extends { [key: string]: number | string | null }>(data: T[]) {
  // Extract headers
  const headers = Object.keys(data[0]);

  // Map the rows
  const rows = data.map((obj) =>
    headers.map((fieldName) => String(obj[fieldName]).replace(/\t/g, " ")).join("\t"),
  );

  // Combine headers and rows
  return [headers.join("\t")].concat(rows).join("\n");
}

/**
 * Takes an array of objects, and returns a simple object map using the specified
 * key and value properties
 *
 * @example
 * const a = [{ id: 1, name: 'foo', age: 10 }, { id: 2, name: 'bar', age: 11 }];
 * const b = arrayToKeyValue(a, 'id', 'name');
 * console.log(b); // { 1: 'foo', 2: 'bar' }
 */
export function arrayToKeyValue<
  T extends Record<K, any>,
  K extends keyof T,
  J extends keyof T,
>(array: T[], keyProperty: K, valueProperty: J): Record<T[K], T[J]> {
  return array.reduce(
    (acc, obj) => {
      const key = obj[keyProperty];
      const value = obj[valueProperty];
      acc[key] = value;
      return acc;
    },
    {} as Record<T[K], T[J]>,
  );
}

// TODO: this function lies about its type output?
export function arrayToMapOfSets<T extends Record<string, any>, K extends keyof T>(
  data: T[],
): Map<K, Set<T[K]>> {
  const mapOfSets = new Map<K, Set<T[K]>>();

  data.forEach((obj) => {
    const entries = Object.entries(obj) as [K, any][];
    entries.forEach(([key, value]: [K, any]) => {
      if (!mapOfSets.has(key)) {
        mapOfSets.set(key, new Set());
      }
      mapOfSets.get(key)?.add(value);
    });
  });

  return mapOfSets as Map<K, Set<T[K]>>;
}

export async function wait({ seconds }: { seconds: number }) {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

export function formatAmericanDate(date: Date): string {
  // WARNING: consider how client side and server side timezones are handled
  // it might cause rehydration to fail if the client and server are in different timezones
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
}

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

export function getRelativeTimeFromSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years !== 0) {
    return rtf.format(-years, "year");
  } else if (months !== 0) {
    return rtf.format(-months, "month");
  } else if (days !== 0) {
    return rtf.format(-days, "day");
  } else if (hours !== 0) {
    return rtf.format(-hours, "hour");
  } else if (minutes !== 0) {
    return rtf.format(-minutes, "minute");
  } else {
    return "just now";
  }
}

export function removeSlashes(value: string) {
  return value.replace(/\//g, "");
}
